import { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, Link } from 'react-router-dom';
import {
  Loader2, Download, Search, ExternalLink, ShieldCheck, Check,
  RefreshCw, Upload, Trash2, ArrowRight,
} from 'lucide-react';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { devError } from '../utils/logger';
import { toast } from 'sonner';

const ADMIN_EMAILS = ['thisisradas@gmail.com'];

const CATEGORY_OPTIONS = [
  'timber', 'bricks', 'tiles', 'stone', 'metal', 'doors', 'windows',
  'fittings', 'lighting', 'sanitaryware', 'hardware', 'other',
];
const CONDITION_OPTIONS = ['excellent', 'good', 'fair', 'for-parts'];
const SOURCE_LABELS = {
  gumtree: 'Gumtree',
  fb_marketplace: 'Facebook Marketplace',
  craigslist: 'Craigslist',
};

/**
 * Admin page at /admin/imports — paste a Gumtree / Facebook Marketplace /
 * Craigslist listing URL, preview what our scraper extracted, tweak the
 * category/price/location, and save it to the public marketplace.
 *
 * External listings are attributed to a system supplier (e.g. "Gumtree
 * (external)"), render with a source badge on their card, and redirect
 * "Contact seller" to the original URL.
 */
export default function AdminImportsPage() {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = !!user && (user.user_type === 'admin' || (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())));

  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  const loadRecent = async () => {
    setLoadingRecent(true);
    try {
      const { data } = await axios.get(`${API}/imports/external-recent`);
      setRecent(data.items || []);
    } catch (e) { devError('imports-recent', e); }
    finally { setLoadingRecent(false); }
  };

  useEffect(() => { if (isAdmin) loadRecent(); }, [isAdmin]);

  if (authLoading) {
    return <div className="min-h-screen pt-24 flex items-center justify-center"><Loader2 className="w-6 h-6 text-accent animate-spin" /></div>;
  }
  if (!isAdmin) return <Navigate to="/" replace />;

  const fetchPreview = async () => {
    if (!url.trim()) { toast.error('Paste a URL first'); return; }
    setFetchingPreview(true);
    try {
      const { data } = await axios.post(`${API}/imports/paste-url/preview`, { url: url.trim() });
      setPreview(data.preview);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not scrape that URL');
    } finally { setFetchingPreview(false); }
  };

  const save = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/imports/paste-url/save`, preview);
      toast.success(data.created ? 'Listing imported into marketplace' : 'Listing updated');
      setPreview(null);
      setUrl('');
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const updatePreview = (key, value) => {
    setPreview((p) => ({ ...p, [key]: value }));
  };

  const removeImage = (idx) => {
    setPreview((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="admin-imports-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2 inline-flex items-center gap-2">
              <Download className="w-3 h-3" /> External imports
            </p>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-2">
              Paste-URL importer
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Paste a Gumtree, Facebook Marketplace or Craigslist listing URL to mirror it into
              PatinaMart's marketplace. External listings carry a source badge and redirect
              "Contact seller" to the original page — we don't try to transact their stock.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/inspirations">
              <Button variant="outline">
                <ArrowRight className="w-4 h-4 mr-2" />
                Inspirations
              </Button>
            </Link>
            <Link to="/admin/suppliers">
              <Button variant="outline">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Supplier queue
              </Button>
            </Link>
          </div>
        </div>

        {/* Paste URL + fetch preview */}
        <div className="rounded-xl border border-border/30 bg-card/30 p-5 md:p-6 mb-8">
          <Label htmlFor="paste-url" className="text-xs uppercase tracking-wider text-muted-foreground">
            Listing URL
          </Label>
          <div className="flex gap-2 mt-2">
            <Input
              id="paste-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.gumtree.com.au/s-ad/.../reclaimed-bricks/..."
              data-testid="paste-url-input"
              className="flex-1 bg-background/40"
            />
            <Button onClick={fetchPreview} disabled={fetchingPreview} data-testid="paste-url-preview-btn">
              {fetchingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-2">Preview</span>
            </Button>
          </div>
        </div>

        {/* Preview panel */}
        {preview && (
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5 md:p-6 mb-8" data-testid="paste-preview">
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-wider text-accent mb-1">
                  {SOURCE_LABELS[preview.external_source] || preview.external_source}
                </p>
                <a href={preview.external_url} target="_blank" rel="noreferrer"
                   className="text-xs text-muted-foreground hover:text-accent inline-flex items-center gap-1">
                  {preview.external_url.slice(0, 80)}... <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPreview(null)} data-testid="preview-cancel">
                Cancel
              </Button>
            </div>

            {/* Images grid — click X to remove */}
            {preview.images?.length > 0 && (
              <div className="mb-5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Images ({preview.images.length})
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {preview.images.map((img, idx) => (
                    <div key={img} className="relative group rounded border border-border/20 overflow-hidden bg-background/40">
                      <img src={img} alt="" className="w-full aspect-square object-cover"
                           onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 w-6 h-6 rounded bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${idx}`}
                        type="button"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Editable fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <Label htmlFor="p-title" className="text-xs">Title</Label>
                <Input id="p-title" value={preview.title} onChange={(e) => updatePreview('title', e.target.value)}
                       data-testid="preview-title" className="bg-background/40" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="p-desc" className="text-xs">Description</Label>
                <Textarea id="p-desc" rows={3} value={preview.description}
                          onChange={(e) => updatePreview('description', e.target.value)}
                          data-testid="preview-description" className="bg-background/40 text-sm" />
              </div>
              <div>
                <Label htmlFor="p-cat" className="text-xs">Category</Label>
                <select id="p-cat" value={preview.category}
                        onChange={(e) => updatePreview('category', e.target.value)}
                        data-testid="preview-category"
                        className="w-full h-10 rounded-md border border-border/30 bg-background/40 px-3 text-sm">
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="p-cond" className="text-xs">Condition</Label>
                <select id="p-cond" value={preview.condition}
                        onChange={(e) => updatePreview('condition', e.target.value)}
                        className="w-full h-10 rounded-md border border-border/30 bg-background/40 px-3 text-sm">
                  {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="p-price" className="text-xs">Price (AUD)</Label>
                <Input id="p-price" type="number" step="0.01" min="0"
                       value={preview.price || 0}
                       onChange={(e) => updatePreview('price', parseFloat(e.target.value) || 0)}
                       data-testid="preview-price" className="bg-background/40" />
              </div>
              <div>
                <Label htmlFor="p-qty" className="text-xs">Quantity</Label>
                <Input id="p-qty" type="number" min="1"
                       value={preview.quantity || 1}
                       onChange={(e) => updatePreview('quantity', parseInt(e.target.value, 10) || 1)}
                       className="bg-background/40" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="p-loc" className="text-xs">Location</Label>
                <Input id="p-loc" value={preview.location || ''}
                       onChange={(e) => updatePreview('location', e.target.value)}
                       data-testid="preview-location" className="bg-background/40"
                       placeholder="e.g. Adelaide, SA" />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-border/20">
              <Button variant="outline" onClick={() => setPreview(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="btn-gold" data-testid="preview-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Save to marketplace
              </Button>
            </div>
          </div>
        )}

        {/* Recent external imports */}
        <div className="rounded-xl border border-border/20 bg-card/20 p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-playfair text-xl">Recent external imports</h2>
            <Button variant="outline" size="sm" onClick={loadRecent} disabled={loadingRecent}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingRecent ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          {loadingRecent ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-accent animate-spin" /></div>
          ) : recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">No external listings yet — paste a URL above to get started.</p>
          ) : (
            <div className="space-y-2" data-testid="recent-imports-list">
              {recent.map((l) => (
                <div key={l.listing_id} className="flex items-center gap-3 rounded-lg border border-border/20 bg-background/30 p-3">
                  {l.images?.[0] ? (
                    <img src={l.images[0]} alt="" className="w-12 h-12 object-cover rounded bg-muted"
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <div className="w-12 h-12 rounded bg-muted" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{l.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {SOURCE_LABELS[l.external_source] || l.external_source} • ${l.price} • {l.location || 'no location'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/listing/${l.listing_id}`} className="text-xs text-accent hover:underline">
                      View
                    </Link>
                    <a href={l.external_url} target="_blank" rel="noreferrer"
                       className="text-xs text-muted-foreground hover:text-accent ml-3 inline-flex items-center gap-1">
                      Source <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground mt-8 flex items-center gap-1.5">
          <Check className="w-3 h-3 text-emerald-400" />
          External listings bypass the supplier verification queue — they're attributed to a
          system supplier (e.g. "Gumtree (external)"). They appear in the marketplace search but
          are hidden from the <Link to="/suppliers" className="text-accent hover:underline">Suppliers map</Link>.
        </p>
      </div>
    </div>
  );
}
